import dayjs from 'dayjs';
import type { TooltipData } from '~/components/nrjx/chart/tooltip/index.vue';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import type { BarChartOptions } from '~/components/nrjx/chart/bar/index.vue';
import type {
  StandardWeeklyResponse,
  StandardOffProductionResponse,
  StandardProductionResponse,
  StandardFabricationOrderResponse,
} from '../types';

dayjs.extend(weekOfYear);

export const useStandardChart = () => {
  const { standardResponse } = storeToRefs(standardStore());
  const { areaId } = storeToRefs(standardStore());

  // Composables
  const { t } = useI18n();
  const { areaProductionUnit, areaFabricationOrderUnit } = coreAreaHelper();
  const { formatDate } = useStandardHelper();
  const { formatNumberToIsoNumber } = useNumbers();

  const graphUnit = computed(() => {
    const type = standardResponse.value?.type;
    if (type === 'weekly') return 'kWh';
    if (type === 'off_production') return 'kW';
    if (type === 'fabrication_order') return `kWh / ${areaFabricationOrderUnit(areaId.value)}`;
    if (type === 'production') return `kWh / ${areaProductionUnit(areaId.value)}`;
    return 'kWh';
  });

  const options = (): BarChartOptions => ({
    height: 'h-[70vh]',
    y: {
      unit: graphUnit.value,
    },
    tooltip: (index) => {
      const response = standardResponse.value;
      const type = response?.type;
      switch (type) {
        case 'weekly':
          return weeklyTooltip(response, index);
        case 'off_production':
          return offProductionTooltip(response, index);
        case 'production':
          return productionTooltip(response, index, 'production');
        case 'fabrication_order':
          return productionTooltip(response, index, 'fabrication_order');
        default:
          return undefined;
      }
    },
  });

  // === Weekly Tooltip ===
  const weeklyTooltip = (data: StandardWeeklyResponse, index: number) => {
    const item = data?.items[index];
    if (!item) return undefined;

    const tooltipData: TooltipData = {
      title: `${formatNumberToIsoNumber(item.event.consumption.quantity)} kWh`,
      subtitle: (() => {
        if (!item.deviation) return undefined;
        const value = Math.abs(item.deviation.percentage);
        if (item.deviation.percentage === 100 || item.deviation.percentage === -100 || item.deviation.percentage === 0) return undefined;
        const precision = item.deviation.percentage < 10 ? 1 : 0;
        return item.deviation.percentage > 0
          ? `+ ${formatNumberToIsoNumber(value, precision)} %`
          : `- ${formatNumberToIsoNumber(value, precision)} %`;
      })(),
      body: [],
      footer: formatDate(item.event.start_date, item.event.end_date),
    };

    if (item.deviation) {
      tooltipData.body?.push({
        title: item.deviation.percentage > 0 ? t('global.drifts') : t('global.win'),
        value: `${formatNumberToIsoNumber(Math.abs(Number(item.deviation.consumption.cost)))} €`,
      });
    } else {
      tooltipData.body = undefined;
    }

    return tooltipData;
  };

  // === Off Production Tooltip ===
  const offProductionTooltip = (data: StandardOffProductionResponse, index: number) => {
    const item = data?.items[index];
    if (!item) return undefined;

    const tooltipData: TooltipData = {
      title: `${formatNumberToIsoNumber(item.event.avg_power)} kW`,
      subtitle: (() => {
        if (!item.deviation) return undefined;
        const value = Math.abs(item.deviation.percentage);
        if (item.deviation.percentage === 100 || item.deviation.percentage === -100 || item.deviation.percentage === 0) return undefined;
        const precision = item.deviation.percentage < 10 ? 1 : 0;
        return item.deviation.percentage > 0
          ? `+ ${formatNumberToIsoNumber(value, precision)} %`
          : `- ${formatNumberToIsoNumber(value, precision)} %`;
      })(),
      body: [],
      footer: formatDate(item.event.start, item.event.end, true),
    };

    if (item.deviation) {
      tooltipData.body?.push({
        title: item.deviation.percentage > 0 ? t('global.drifts') : t('global.win'),
        value: `${formatNumberToIsoNumber(Math.abs(Number(item.deviation.consumption.cost)))} €`,
      });
    } else {
      tooltipData.body = undefined;
    }

    return tooltipData;
  };

  // === Production Tooltip ===
  const productionTooltip = (
    data: StandardProductionResponse | StandardFabricationOrderResponse,
    index: number,
    type: 'production' | 'fabrication_order',
  ) => {
    const item = data?.items[index];
    if (!item) return undefined;

    const tooltipData: TooltipData = {
      title: `${formatNumberToIsoNumber(item.event.consumption_per_unit.quantity, 2)} kWh / ${
        type === 'production' ? areaProductionUnit(areaId.value) : areaFabricationOrderUnit(areaId.value)
      }`,
      subtitle: (() => {
        if (!item.deviation) return undefined;
        const value = Math.abs(item.deviation.percentage);
        if (item.deviation.percentage === 100 || item.deviation.percentage === -100 || item.deviation.percentage === 0) return undefined;
        const precision = item.deviation.percentage < 10 ? 1 : 0;
        return item.deviation.percentage > 0
          ? `+ ${formatNumberToIsoNumber(value, precision)} %`
          : `- ${formatNumberToIsoNumber(value, precision)} %`;
      })(),
      body: [
        {
          title: t('global.production'),
          value: `${formatNumberToIsoNumber(Number(item.event.production.quantity))} ${
            type === 'production' ? areaProductionUnit(areaId.value) : areaFabricationOrderUnit(areaId.value)
          }`,
        },
        {
          title: t('global.consumption'),
          value: `${formatNumberToIsoNumber(Number(item.event.consumption_per_unit.quantity), 2)} kWh`,
        },
        { title: t('global.average_power'), value: `${formatNumberToIsoNumber(Number(item.event.avg_power))} kW` },
      ],
      footer: formatDate(item.event.production.start, item.event.production.end, type === 'fabrication_order'),
    };

    if (item.deviation) {
      tooltipData.body?.push({
        title: item.deviation.percentage > 0 ? t('global.drifts') : t('global.win'),
        value: `${formatNumberToIsoNumber(Math.abs(Number(item.deviation.consumption.cost)))} €`,
      });
    }
    return tooltipData;
  };

  return {
    options,
  };
};
